import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';

// Firestore admite hasta 30 valores por cláusula `in`. Un productor con más
// clientes que eso necesita varias consultas, que acá se fusionan en una sola
// lista para que el resto de la app no se entere.
export const IN_QUERY_LIMIT = 30;

/**
 * Parte la lista de comitentes en bloques aptos para una cláusula `in`,
 * descartando vacíos y duplicados.
 */
export const chunkComitentes = (comitentes = [], size = IN_QUERY_LIMIT) => {
  const unicos = [...new Set((comitentes || []).filter(Boolean).map(String))];
  const chunks = [];
  for (let i = 0; i < unicos.length; i += size) {
    chunks.push(unicos.slice(i, i + size));
  }
  return chunks;
};

/**
 * Suscribe a `collectionName` restringida a los comitentes indicados
 * (`where nroComitente in [...]`), fusionando los bloques en una única lista.
 *
 * Se usa en modo productor: las reglas de Firestore solo le permiten leer los
 * documentos cuyo `nroComitente` está en su padrón, así que la consulta tiene
 * que declarar esa restricción; una lectura de la colección completa sería
 * rechazada. No se agregan otros filtros (estado, plazo) para no requerir
 * índices compuestos: ese filtrado se hace en memoria.
 *
 * `onData` recibe la lista completa recién cuando todos los bloques
 * respondieron al menos una vez, para no emitir resultados parciales.
 *
 * @returns {() => void} función de desuscripción
 */
export const subscribeByComitentes = (collectionName, comitentes, onData, onError) => {
  const chunks = chunkComitentes(comitentes);
  if (chunks.length === 0) {
    onData([]);
    return () => {};
  }

  const partials = new Map();
  const emit = () => {
    if (partials.size < chunks.length) return;
    onData(Array.from(partials.values()).flat());
  };

  const unsubs = chunks.map((chunk, index) => onSnapshot(
    query(collection(db, collectionName), where('nroComitente', 'in', chunk)),
    (snapshot) => {
      partials.set(index, snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      emit();
    },
    (error) => {
      console.error(`comitentesQueryService: error suscribiendo ${collectionName}:`, error);
      if (onError) onError(error);
    },
  ));

  return () => unsubs.forEach(unsub => unsub());
};
